"use client";

// Live current-screen detail (v4): what the player sees on the event and shop
// screens. Event text is localized by the mod and carries the game's BBCode
// tags ([gold]/[sine]/[jitter]/...), rendered via RichDescriptionSimple.
// Shop item ids resolve to names/images through the run-page pills (hover shows
// the full card / relic tooltip). Contract: markdown-docs/live-presence.md (v4).
import { c as _c } from "react/compiler-runtime";
import { Link } from "@/i18n/navigation";
import { useT } from "@/lib/i18n";
import { imageUrl } from "@/lib/image-url";
import { RichDescriptionSimple } from "@/app/components/RichDescription";
import { CardPill, PotionPill, RelicPill, cleanId, displayName, type CardInfo, type PotionInfo, type RelicInfo } from "../runs/[hash]/RunPills";
import { LiveCardImg, parseDeckId, safeId, withOrdinalKeys, type LiveEventCtx, type LiveLoot, type LiveShop, type NamedMap, type ShopItem } from "./live-shared";
function Gold(t0) {
  const $ = _c(2);
  const {
    cost
  } = t0;
  if (cost == null) {
    return null;
  }
  let t1;
  if ($[0] !== cost) {
    t1 = <span className="text-[var(--accent-gold)] tabular-nums font-semibold">{cost}g</span>;
    $[0] = cost;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
}
export function LiveEventPanel(t0) {
  const $ = _c(30);
  const {
    ev,
    bp,
    cards,
    relics,
    events
  } = t0;
  const t = useT();
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== bp || $[1] !== ev.id || $[2] !== ev.title || $[3] !== events || $[4] !== t) {
    const id = cleanId(ev.id);
    const titleText = events?.[id]?.name || ev.title || displayName(`EVENT.${id}`);
    t4 = "rounded-lg border border-special/30 bg-special/10 p-4";
    t1 = "flex items-center gap-2 mb-2";
    let t5;
    if ($[9] !== t) {
      t5 = t("Event");
      $[9] = t;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    if ($[11] !== t5) {
      t2 = <span className="text-[10px] font-bold uppercase tracking-wider text-special">{t5}</span>;
      $[11] = t5;
      $[12] = t2;
    } else {
      t2 = $[12];
    }
    t3 = safeId(id) ? <Link href={`${bp}/events/${id.toLowerCase()}`} className="text-sm font-semibold text-[var(--text-primary)] hover:text-[var(--accent-gold)]">{titleText}</Link> : <span className="text-sm font-semibold text-[var(--text-primary)]">{titleText}</span>;
    $[0] = bp;
    $[1] = ev.id;
    $[2] = ev.title;
    $[3] = events;
    $[4] = t;
    $[5] = t1;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t1 = $[5];
    t2 = $[6];
    t3 = $[7];
    t4 = $[8];
  }
  let t5;
  if ($[13] !== t1 || $[14] !== t2 || $[15] !== t3) {
    t5 = <div className={t1}>{t2}{t3}</div>;
    $[13] = t1;
    $[14] = t2;
    $[15] = t3;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== ev.prompt) {
    t6 = ev.prompt && <p className="text-sm text-[var(--text-secondary)] whitespace-pre-line mb-3 leading-relaxed"><RichDescriptionSimple text={ev.prompt} /></p>;
    $[17] = ev.prompt;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== bp || $[20] !== cards || $[21] !== ev.options || $[22] !== relics || $[23] !== t) {
    t7 = (ev.options?.length ?? 0) > 0 && <ul className="space-y-1.5">{withOrdinalKeys((ev.options ?? []).map(_temp)).map((t8, i_0) => {
        const {
          key
        } = t8;
        const o_0 = (ev.options ?? [])[i_0];
        const disabled = o_0.locked;
        return <li key={key} className={`flex items-start gap-2 rounded-md border px-2.5 py-1.5 text-sm ${o_0.chosen ? "border-[var(--accent-gold)] bg-[var(--bg-primary)] text-[var(--text-primary)] ring-1 ring-[var(--accent-gold)]" : disabled ? "border-[var(--border-subtle)] bg-[var(--bg-primary)] text-[var(--text-muted)]" : "border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)]"}`}><span className="mt-0.5 shrink-0">{o_0.chosen ? "\u2713" : disabled ? "\uD83D\uDD12" : "\u2022"}</span><span className={`min-w-0 flex-1 ${disabled ? "line-through" : ""}`}>{o_0.text ? <RichDescriptionSimple text={o_0.text} /> : o_0.key || t("(option)")}{o_0.desc && o_0.desc !== o_0.text && <span className="mt-0.5 block text-xs text-[var(--text-muted)]"><RichDescriptionSimple text={o_0.desc} /></span>}{(o_0.card || o_0.relic) && <span className="mt-1.5 flex flex-wrap items-center gap-2">{o_0.card && <CardPill cardId={cleanId(o_0.card)} cardData={cards ?? {}} bp={bp} className="block w-16 shrink-0"><LiveCardImg id={cleanId(o_0.card)} alt="" className="w-16 rounded shadow" portrait={cards?.[cleanId(o_0.card)]?.image_url} /></CardPill>}{o_0.relic && <RelicPill relicId={cleanId(o_0.relic)} relicData={relics ?? {}} bp={bp} className="block shrink-0"><img src={imageUrl(relics?.[cleanId(o_0.relic)]?.image_url || `/static/images/relics/${cleanId(o_0.relic).toLowerCase()}.png`)} alt="" className="h-9 w-9 object-contain" crossOrigin="anonymous" onError={_temp2} /></RelicPill>}</span>}</span>{o_0.proceed && !o_0.chosen && <span className="text-[10px] text-[var(--text-muted)] shrink-0">{t("leave")}</span>}</li>;
      })}</ul>;
    $[19] = bp;
    $[20] = cards;
    $[21] = ev.options;
    $[22] = relics;
    $[23] = t;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t4 || $[26] !== t5 || $[27] !== t6 || $[28] !== t7) {
    t8 = <div className={t4}>{t5}{t6}{t7}</div>;
    $[25] = t4;
    $[26] = t5;
    $[27] = t6;
    $[28] = t7;
    $[29] = t8;
  } else {
    t8 = $[29];
  }
  return t8;
}
function _temp2(e) {
  (e.target as HTMLImageElement).style.display = "none";
}
function _temp(o, i) {
  return o.key || o.text || String(i);
}
function ShopSection(t0) {
  const $ = _c(20);
  const {
    title,
    items,
    kind,
    cards,
    relics,
    potions,
    bp
  } = t0;
  const t = useT();
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== bp || $[1] !== cards || $[2] !== items || $[3] !== kind || $[4] !== potions || $[5] !== relics || $[6] !== t || $[7] !== title) {
    t4 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const real = items.filter(_temp3);
      if (real.length === 0) {
        t4 = null;
        break bb0;
      }
      if ($[12] !== title) {
        t3 = <div className="text-[10px] uppercase tracking-wider text-[var(--text-muted)] mb-1.5">{title}</div>;
        $[12] = title;
        $[13] = t3;
      } else {
        t3 = $[13];
      }
      t1 = "space-y-1";
      t2 = withOrdinalKeys(real.map(_temp4)).map((t5, idx) => {
        const {
          key
        } = t5;
        const it_1 = real[idx];
        const rawId = it_1.id as string;
        const {
          id,
          upgraded
        } = parseDeckId(rawId);
        const sold = it_1.stocked === false;
        const info = kind === "card" ? cards[id] : kind === "relic" ? relics[id] : potions[id];
        const name = info?.name || displayName(`${kind.toUpperCase()}.${id}`);
        const portrait = info?.image_url ? imageUrl(info.image_url) : "";
        const thumb = kind === "card" ? <LiveCardImg id={id} upgraded={upgraded} alt={name} className="w-7 h-auto rounded-sm" portrait={info?.image_url} /> : <img src={portrait || imageUrl(`/static/images/${kind === "relic" ? "relics" : "potions"}/${id.toLowerCase()}.png`)} alt={name} className="w-7 h-7 object-contain" crossOrigin="anonymous" loading="lazy" onError={_temp5} />;
        const pill = kind === "card" ? <CardPill cardId={id} upgraded={upgraded} cardData={cards} bp={bp} className="block shrink-0">{thumb}</CardPill> : kind === "relic" ? <RelicPill relicId={id} relicData={relics} bp={bp} className="block shrink-0">{thumb}</RelicPill> : <PotionPill potionId={id} potionData={potions} bp={bp} className="block shrink-0">{thumb}</PotionPill>;
        return <li key={key} className={`flex items-center gap-2 ${sold ? "opacity-40" : ""}`}>{pill}<span className={`text-sm min-w-0 flex-1 truncate ${sold ? "line-through" : "text-[var(--text-secondary)]"}`}>{name}{upgraded ? "+" : ""}</span>{it_1.on_sale && !sold && <span className="text-[9px] font-bold uppercase rounded bg-success-fill px-1 text-on-fill shrink-0">{t("sale")}</span>}{sold ? <span className="text-[10px] text-[var(--text-muted)] shrink-0">{t("sold")}</span> : <Gold cost={it_1.cost} />}</li>;
      });
    }
    $[0] = bp;
    $[1] = cards;
    $[2] = items;
    $[3] = kind;
    $[4] = potions;
    $[5] = relics;
    $[6] = t;
    $[7] = title;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
  } else {
    t1 = $[8];
    t2 = $[9];
    t3 = $[10];
    t4 = $[11];
  }
  if (t4 !== Symbol.for("react.early_return_sentinel")) {
    return t4;
  }
  let t5;
  if ($[14] !== t1 || $[15] !== t2) {
    t5 = <ul className={t1}>{t2}</ul>;
    $[14] = t1;
    $[15] = t2;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== t3 || $[18] !== t5) {
    t6 = <div>{t3}{t5}</div>;
    $[17] = t3;
    $[18] = t5;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  return t6;
}
function _temp5(e) {
  (e.target as HTMLImageElement).style.visibility = "hidden";
}
function _temp4(it_0) {
  return it_0.id as string;
}
function _temp3(it) {
  return it.id;
}
export function LiveShopPanel(t0) {
  const $ = _c(46);
  const {
    shop,
    cards,
    relics,
    potions,
    bp
  } = t0;
  const t = useT();
  const removal = shop.removal;
  let t1;
  if ($[0] !== t) {
    t1 = t("Shop");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1) {
    t2 = <div className="text-[10px] font-bold uppercase tracking-wider text-[var(--accent-gold)]">{t1}</div>;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== t) {
    t3 = t("Cards");
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== shop.cards) {
    t4 = shop.cards ?? [];
    $[6] = shop.cards;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== bp || $[9] !== cards || $[10] !== potions || $[11] !== relics || $[12] !== t3 || $[13] !== t4) {
    t5 = <ShopSection title={t3} items={t4} kind="card" cards={cards} relics={relics} potions={potions} bp={bp} />;
    $[8] = bp;
    $[9] = cards;
    $[10] = potions;
    $[11] = relics;
    $[12] = t3;
    $[13] = t4;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t) {
    t6 = t("Relics");
    $[15] = t;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== shop.relics) {
    t7 = shop.relics ?? [];
    $[17] = shop.relics;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== bp || $[20] !== cards || $[21] !== potions || $[22] !== relics || $[23] !== t6 || $[24] !== t7) {
    t8 = <ShopSection title={t6} items={t7} kind="relic" cards={cards} relics={relics} potions={potions} bp={bp} />;
    $[19] = bp;
    $[20] = cards;
    $[21] = potions;
    $[22] = relics;
    $[23] = t6;
    $[24] = t7;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t) {
    t9 = t("Potions");
    $[26] = t;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== shop.potions) {
    t10 = shop.potions ?? [];
    $[28] = shop.potions;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] !== bp || $[31] !== cards || $[32] !== potions || $[33] !== relics || $[34] !== t10 || $[35] !== t9) {
    t11 = <ShopSection title={t9} items={t10} kind="potion" cards={cards} relics={relics} potions={potions} bp={bp} />;
    $[30] = bp;
    $[31] = cards;
    $[32] = potions;
    $[33] = relics;
    $[34] = t10;
    $[35] = t9;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  let t12;
  if ($[37] !== removal || $[38] !== t) {
    t12 = removal && removal.cost != null && <div className="flex items-center gap-2 pt-1 border-t border-[var(--border-subtle)]"><span className={`text-sm flex-1 ${removal.stocked === false ? "text-[var(--text-muted)] line-through" : "text-[var(--text-secondary)]"}`}>{t("Card removal")}</span>{removal.stocked === false ? <span className="text-[10px] text-[var(--text-muted)]">{t("used")}</span> : <Gold cost={removal.cost} />}</div>;
    $[37] = removal;
    $[38] = t;
    $[39] = t12;
  } else {
    t12 = $[39];
  }
  let t13;
  if ($[40] !== t11 || $[41] !== t12 || $[42] !== t2 || $[43] !== t5 || $[44] !== t8) {
    t13 = <div className="rounded-lg border border-[var(--accent-gold)]/30 bg-[var(--bg-card)] p-4 space-y-3">{t2}{t5}{t8}{t11}{t12}</div>;
    $[40] = t11;
    $[41] = t12;
    $[42] = t2;
    $[43] = t5;
    $[44] = t8;
    $[45] = t13;
  } else {
    t13 = $[45];
  }
  return t13;
}

/** The combat/reward-screen loot: gold plus the cards (full renders), relics,
 * and potions on offer, each with the run-page hover tooltip. Potions on top,
 * cards + relics below. */
export function LiveLootPanel(t0) {
  const $ = _c(45);
  const {
    loot,
    cards,
    relics,
    potions,
    bp
  } = t0;
  const t = useT();
  let removal;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  if ($[0] !== bp || $[1] !== cards || $[2] !== loot.card_removal || $[3] !== loot.cards || $[4] !== loot.gold || $[5] !== loot.packs || $[6] !== loot.potions || $[7] !== loot.relics || $[8] !== potions || $[9] !== relics || $[10] !== t) {
    t6 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const cardIds = loot.cards ?? [];
      const relicIds = loot.relics ?? [];
      const potionIds = loot.potions ?? [];
      let t7;
      if ($[18] !== loot.packs) {
        t7 = loot.packs ?? [];
        $[18] = loot.packs;
        $[19] = t7;
      } else {
        t7 = $[19];
      }
      const packs = t7;
      const hasGold = loot.gold != null && loot.gold > 0;
      removal = !!loot.card_removal;
      if (!cardIds.length && !relicIds.length && !potionIds.length && !packs.length && !hasGold && !removal) {
        t6 = null;
        break bb0;
      }
      t1 = "rounded-lg border border-warning/30 bg-warning/10 p-4";
      let t8;
      if ($[20] !== t) {
        t8 = t("Rewards");
        $[20] = t;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      let t9;
      if ($[22] !== t8) {
        t9 = <span className="text-[10px] font-bold uppercase tracking-wider text-warning">{t8}</span>;
        $[22] = t8;
        $[23] = t9;
      } else {
        t9 = $[23];
      }
      let t10;
      if ($[24] !== hasGold || $[25] !== loot.gold) {
        t10 = hasGold && <span className="text-sm font-semibold tabular-nums text-[var(--accent-gold)]">{loot.gold}g</span>;
        $[24] = hasGold;
        $[25] = loot.gold;
        $[26] = t10;
      } else {
        t10 = $[26];
      }
      if ($[27] !== t10 || $[28] !== t9) {
        t2 = <div className="mb-2 flex items-center gap-2">{t9}{t10}</div>;
        $[27] = t10;
        $[28] = t9;
        $[29] = t2;
      } else {
        t2 = $[29];
      }
      t3 = potionIds.length > 0 && <div className="mb-2 flex flex-wrap gap-1.5">{withOrdinalKeys(potionIds).map(t11 => {
          const {
            item,
            key
          } = t11;
          const id = cleanId(item);
          const info = potions[id];
          return <PotionPill key={`p-${key}`} potionId={id} potionData={potions} bp={bp} className="block shrink-0">{info?.image_url ? <img src={imageUrl(info.image_url)} alt={info.name} className="h-9 w-9 object-contain" crossOrigin="anonymous" loading="lazy" onError={_temp6} /> : <span className="text-xs text-[var(--text-secondary)]">{displayName(`POTION.${id}`)}</span>}</PotionPill>;
        })}</div>;
      if ($[30] !== bp || $[31] !== cards || $[32] !== packs || $[33] !== t) {
        t4 = packs.length > 0 && <div className="space-y-2">{packs.map((pack, pi) => <div key={`pack-${pi}`}><div className="mb-1 text-[10px] font-bold uppercase tracking-wide text-warning/80">{t("Pack {n}", {
                n: pi + 1
              })}</div><div className="flex flex-wrap items-start gap-1.5">{withOrdinalKeys(pack).map(t12 => {
                const {
                  item: item_0,
                  key: key_0
                } = t12;
                const {
                  id: id_0,
                  upgraded
                } = parseDeckId(item_0);
                return <CardPill key={`pk${pi}-${key_0}`} cardId={id_0} upgraded={upgraded} cardData={cards} bp={bp} className="relative block w-16 shrink-0"><LiveCardImg id={id_0} upgraded={upgraded} alt={cards[id_0]?.name || displayName(`CARD.${id_0}`)} className="h-auto w-16 rounded-sm" portrait={cards[id_0]?.image_url} /></CardPill>;
              })}</div></div>)}</div>;
        $[30] = bp;
        $[31] = cards;
        $[32] = packs;
        $[33] = t;
        $[34] = t4;
      } else {
        t4 = $[34];
      }
      t5 = (!packs.length && cardIds.length > 0 || relicIds.length > 0) && <div className="flex flex-wrap items-start gap-1.5">{!packs.length && withOrdinalKeys(cardIds).map(t12 => {
          const {
            item: item_1,
            key: key_1
          } = t12;
          const {
            id: id_1,
            upgraded: upgraded_0
          } = parseDeckId(item_1);
          return <CardPill key={`c-${key_1}`} cardId={id_1} upgraded={upgraded_0} cardData={cards} bp={bp} className="relative block w-16 shrink-0"><LiveCardImg id={id_1} upgraded={upgraded_0} alt={cards[id_1]?.name || displayName(`CARD.${id_1}`)} className="h-auto w-16 rounded-sm" portrait={cards[id_1]?.image_url} /></CardPill>;
        })}{withOrdinalKeys(relicIds).map(t13 => {
          const {
            item: item_2,
            key: key_2
          } = t13;
          const id_2 = cleanId(item_2);
          const info_0 = relics[id_2];
          return <RelicPill key={`r-${key_2}`} relicId={id_2} relicData={relics} bp={bp} className="block shrink-0">{info_0?.image_url ? <img src={imageUrl(info_0.image_url)} alt={info_0.name} className="h-9 w-9 object-contain" crossOrigin="anonymous" loading="lazy" onError={_temp7} /> : <span className="text-xs text-[var(--text-secondary)]">{displayName(`RELIC.${id_2}`)}</span>}</RelicPill>;
        })}</div>;
    }
    $[0] = bp;
    $[1] = cards;
    $[2] = loot.card_removal;
    $[3] = loot.cards;
    $[4] = loot.gold;
    $[5] = loot.packs;
    $[6] = loot.potions;
    $[7] = loot.relics;
    $[8] = potions;
    $[9] = relics;
    $[10] = t;
    $[11] = removal;
    $[12] = t1;
    $[13] = t2;
    $[14] = t3;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    removal = $[11];
    t1 = $[12];
    t2 = $[13];
    t3 = $[14];
    t4 = $[15];
    t5 = $[16];
    t6 = $[17];
  }
  if (t6 !== Symbol.for("react.early_return_sentinel")) {
    return t6;
  }
  let t7;
  if ($[35] !== removal || $[36] !== t) {
    t7 = removal && <div className="mt-2 text-xs text-[var(--text-muted)]">{t("Card removal available")}</div>;
    $[35] = removal;
    $[36] = t;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  let t8;
  if ($[38] !== t1 || $[39] !== t2 || $[40] !== t3 || $[41] !== t4 || $[42] !== t5 || $[43] !== t7) {
    t8 = <div className={t1}>{t2}{t3}{t4}{t5}{t7}</div>;
    $[38] = t1;
    $[39] = t2;
    $[40] = t3;
    $[41] = t4;
    $[42] = t5;
    $[43] = t7;
    $[44] = t8;
  } else {
    t8 = $[44];
  }
  return t8;
}
function _temp7(e_0) {
  (e_0.target as HTMLImageElement).style.display = "none";
}
function _temp6(e) {
  (e.target as HTMLImageElement).style.display = "none";
}